import { useState, useEffect } from 'react';
import { createApiClient, type N8nCredentialSchema } from '@n8n-builder/api-client';
import { API_BASE_URL } from '../../lib/api-config';
import { useCredentialsStore } from '../../store/credentials';
import { SpinnerIcon } from '../ui/icons';

interface CredentialFormProps {
  credentialType: string;
  onCreated: (credential: { id: string; name: string }) => void;
  onCancel: () => void;
}

function humanizeType(type: string): string {
  return type
    .replace(/Api$/, ' API')
    .replace(/OAuth2$/, ' OAuth2')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase());
}

function humanizeFieldName(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase());
}

function isSensitiveField(name: string): boolean {
  return /key|secret|token|password|apikey/i.test(name);
}

const inputClasses = 'w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500';

export function CredentialForm({ credentialType, onCreated, onCancel }: CredentialFormProps) {
  const { schemas, addSchema } = useCredentialsStore();
  const [schema, setSchema] = useState<N8nCredentialSchema | null>(schemas[credentialType] || null);
  const [loading, setLoading] = useState(!schema);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState(humanizeType(credentialType));
  const [formData, setFormData] = useState<Record<string, unknown>>({});

  useEffect(() => {
    if (schema) return;

    let cancelled = false;
    (async () => {
      try {
        const apiClient = createApiClient(API_BASE_URL);
        const result = await apiClient.getCredentialSchema(credentialType);
        if (cancelled) return;
        if (result.error || !result.data?.schema) {
          setError(result.error || 'Failed to load schema');
          setLoading(false);
          return;
        }
        const s = result.data.schema;
        setSchema(s);
        addSchema(credentialType, s);

        // Pre-fill defaults
        const defaults: Record<string, unknown> = {};
        if (s.properties) {
          for (const [key, prop] of Object.entries(s.properties)) {
            if (prop.default !== undefined) {
              defaults[key] = prop.default;
            }
          }
        }
        setFormData(defaults);
      } catch {
        if (!cancelled) setError('Failed to load credential schema');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [credentialType, schema, addSchema]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const apiClient = createApiClient(API_BASE_URL);
      const result = await apiClient.createN8nCredential(name, credentialType, formData);
      if (result.error || !result.data?.credential) {
        setError(result.error || 'Failed to create credential');
        setSubmitting(false);
        return;
      }
      onCreated({ id: result.data.credential.id, name: result.data.credential.name });
    } catch {
      setError('Failed to create credential');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-3 text-sm text-gray-500">
        <SpinnerIcon className="h-4 w-4" />
        Loading schema...
      </div>
    );
  }

  if (error && !schema) {
    return (
      <div className="p-3">
        <p className="text-sm text-red-600 mb-2">{error}</p>
        <button onClick={onCancel} className="text-sm text-gray-500 hover:text-gray-700">
          Cancel
        </button>
      </div>
    );
  }

  const properties = schema?.properties || {};
  const required = new Set(schema?.required || []);

  return (
    <form onSubmit={handleSubmit} className="border-t border-gray-100 bg-gray-50/50 p-3 space-y-3">
      {/* Credential name */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          Name <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          className={inputClasses}
        />
      </div>

      {/* Schema fields */}
      {Object.entries(properties).map(([key, prop]) => {
        const isRequired = required.has(key);
        const fieldType = prop.type;
        const label = humanizeFieldName(key);

        if (fieldType === 'boolean') {
          return (
            <div key={key} className="flex items-center gap-2">
              <input
                type="checkbox"
                id={key}
                checked={!!formData[key]}
                onChange={(e) => setFormData((prev) => ({ ...prev, [key]: e.target.checked }))}
                className="rounded border-gray-200 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor={key} className="text-xs font-medium text-gray-700">
                {label}
                {isRequired && <span className="text-red-400 ml-0.5">*</span>}
              </label>
            </div>
          );
        }

        if (prop.enum && prop.enum.length > 0) {
          return (
            <div key={key}>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {label}
                {isRequired && <span className="text-red-400 ml-0.5">*</span>}
              </label>
              <select
                value={(formData[key] as string) || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, [key]: e.target.value }))}
                required={isRequired}
                className={inputClasses}
              >
                <option value="">Select...</option>
                {prop.enum.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
              {prop.description && (
                <p className="mt-0.5 text-xs text-gray-400">{prop.description}</p>
              )}
            </div>
          );
        }

        if (fieldType === 'number') {
          return (
            <div key={key}>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {label}
                {isRequired && <span className="text-red-400 ml-0.5">*</span>}
              </label>
              <input
                type="number"
                value={(formData[key] as number) ?? ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, [key]: Number(e.target.value) }))}
                required={isRequired}
                className={inputClasses}
              />
              {prop.description && (
                <p className="mt-0.5 text-xs text-gray-400">{prop.description}</p>
              )}
            </div>
          );
        }

        // Default: string input
        return (
          <div key={key}>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {label}
              {isRequired && <span className="text-red-400 ml-0.5">*</span>}
            </label>
            <input
              type={isSensitiveField(key) ? 'password' : 'text'}
              value={(formData[key] as string) || ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, [key]: e.target.value }))}
              required={isRequired}
              placeholder={prop.description || ''}
              className={inputClasses}
            />
            {prop.description && (
              <p className="mt-0.5 text-xs text-gray-400">{prop.description}</p>
            )}
          </div>
        );
      })}

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          disabled={submitting || !name.trim()}
          className="flex-1 flex items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <>
              <SpinnerIcon className="h-3.5 w-3.5" />
              Creating...
            </>
          ) : (
            'Create Credential'
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
